import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Instagram } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { SplashScreen } from "@/components/SplashScreen";
import { photoUrl } from "@/components/Portfolio";
import { DEFAULT_SERVICES_PAGE } from "@/lib/content";
import { useTexts, useExtras } from "@/lib/texts";

const EASE = [0.16, 1, 0.3, 1];

export default function Servizi() {
    const texts = useTexts();
    const { instagram } = useExtras();
    const [blocks, setBlocks] = useState(DEFAULT_SERVICES_PAGE);

    useEffect(() => {
        window.scrollTo(0, 0);
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
            .then((res) => {
                if (Array.isArray(res.data?.services_page) && res.data.services_page.length) {
                    setBlocks(res.data.services_page);
                }
            })
            .catch(() => {});
    }, []);

    return (
        <div data-testid="servizi-page" className="min-h-screen bg-[#F9F7F2]">
            <SplashScreen />
            <header className="border-b border-stone-900/10 bg-[rgba(249,247,242,0.9)] backdrop-blur-md">
                <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-4 sm:px-8">
                    <Link to="/" data-testid="servizi-logo">
                        <LogoFull />
                    </Link>
                    <div className="flex items-center gap-4">
                        <a
                            href={instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="servizi-instagram-link"
                            aria-label="Seguici su Instagram"
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-900/15 text-stone-600 transition-colors duration-300 hover:border-[#E75C15] hover:text-[#E75C15]"
                        >
                            <Instagram className="h-4 w-4" />
                        </a>
                        <Link
                            to="/"
                            data-testid="servizi-back-home"
                            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-stone-500 transition-colors hover:text-[#B8430D]"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            Torna alla Home
                        </Link>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-16 sm:px-8 sm:py-24">
                <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]"
                >
                    Cosa Facciamo
                </motion.span>
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                    className="mt-4 max-w-3xl font-display text-4xl font-light tracking-tight text-stone-900 sm:text-5xl lg:text-6xl"
                >
                    {texts.servizi_page_title}
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                    className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg"
                >
                    {texts.servizi_page_intro}
                </motion.p>

                <div className="mt-16 space-y-16 sm:mt-24 sm:space-y-24">
                    {blocks.map((b, i) => (
                        <motion.article
                            key={i}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE }}
                            data-testid={`servizi-block-${i}`}
                            className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14`}
                        >
                            {b.photo ? (
                                <div className={`relative overflow-hidden lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
                                    <img
                                        src={photoUrl(b.photo)}
                                        alt={b.title}
                                        loading="lazy"
                                        decoding="async"
                                        className="aspect-[4/3] w-full object-cover"
                                    />
                                    <span className="absolute left-4 top-4 bg-[#1C1917]/80 px-3 py-1 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#E75C15] backdrop-blur-sm">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                            ) : null}
                            <div className={b.photo ? "lg:col-span-6" : "lg:col-span-8 lg:col-start-3"}>
                                {!b.photo && (
                                    <span className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                )}
                                <h2 className="mt-2 font-display text-3xl font-light tracking-tight text-stone-900 sm:text-4xl">
                                    {b.title}
                                </h2>
                                <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-600">
                                    {b.text}
                                </p>
                                <Link
                                    to="/#contatti"
                                    data-testid={`servizi-cta-${i}`}
                                    className="group mt-7 inline-flex items-center gap-2 border-b border-[#B8430D]/50 pb-1 text-xs font-medium uppercase tracking-wider text-[#B8430D] transition-colors hover:text-stone-900"
                                >
                                    Richiedi un preventivo
                                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
                                </Link>
                            </div>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="mt-24 border border-stone-900/10 bg-[#1C1917] p-10 text-center sm:p-14"
                >
                    <h2 className="font-display text-3xl font-light text-stone-100 sm:text-4xl">
                        Hai un progetto in mente?
                    </h2>
                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-stone-400">
                        Sopralluogo gratuito e preventivo analitico senza impegno, in tutta la
                        Toscana e oltre.
                    </p>
                    <Link
                        to="/#contatti"
                        data-testid="servizi-cta-final"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E75C15] px-8 py-4 text-sm font-medium text-[#1C1917] transition-colors duration-300 hover:bg-stone-100"
                    >
                        Contattaci Ora
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </motion.div>
            </main>

            <Footer />
        </div>
    );
}
